import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArchiveIcon, ChevronDownIcon, ClockIcon, ImageIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { CreativeSet, CreativeStatus, creativeSets } from '../data/adStudio';
const groups: {
  status: CreativeStatus;
  description: string;
}[] = [{
  status: 'Draft',
  description: 'Carli is still shaping these. Nothing has been sent out.'
}, {
  status: 'In review',
  description: 'Waiting on brand or internal sign-off.'
}, {
  status: 'Approved',
  description: 'Live now across their approved placements.'
}, {
  status: 'Past',
  description: 'Flights that have ended. Kept for reference until you delete them.'
}];
const statusStyles: Record<CreativeStatus, string> = {
  Draft: 'bg-ink/7 text-ink/60',
  'In review': 'bg-[#C9A04E]/18 text-[#8B6A20]',
  Approved: 'bg-positive/12 text-positive',
  Past: 'bg-ink/10 text-ink/55'
};
export function AdStudioHome({
  selectedOwner,
  onOpen
}: {
  selectedOwner: string;
  onOpen: (set: CreativeSet) => void;
}) {
  const [allSets, setAllSets] = useState<CreativeSet[]>(creativeSets);
  const sets = selectedOwner ? allSets.filter(set => set.brand === selectedOwner) : allSets;
  const [collapsed, setCollapsed] = useState<Record<CreativeStatus, boolean>>({
    Draft: false,
    'In review': false,
    Approved: false,
    Past: true
  });
  const endFlight = (id: string) => {
    setAllSets(current => current.map(set => set.id === id ? {
      ...set,
      status: 'Past',
      updated: 'Ended just now',
      ranWindow: set.ranWindow ?? 'Flight closed manually',
      result: set.result ?? 'Results still reconciling'
    } : set));
    setCollapsed(current => ({
      ...current,
      Past: false
    }));
  };
  const deleteSet = (id: string) => setAllSets(current => current.filter(set => set.id !== id));
  return <section aria-labelledby="ad-studio-heading" data-id="element-148" data-mp="1e9zhmlyebbxy">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-149" data-mp="1ebsskdg1kibr">
        <div data-id="element-150" data-mp="1o4wxyybtmlo8">
          <h2 id="ad-studio-heading" className="font-display text-2xl font-medium tracking-tight" data-id="element-151" data-mp="1o3c65ggw5gu2">Ad studio</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-152" data-mp="1e7ihwztt7ywg">
            Carli briefs, generates, and adapts paid creative across every placement. You review and approve.
          </p>
        </div>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#A34E33] sm:self-auto" data-id="element-153" data-mp="157cb1wsm9x69">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-154" data-mp="vhalf0km86p8" />
          New creative set
        </button>
      </div>

      {sets.length === 0 ? <div className="mt-8 rounded-2xl border border-dashed border-ink/15 bg-cream/45 px-6 py-14 text-center" data-id="element-155" data-mp="mg2wva7wik8c">
          <CarliStars className="mx-auto h-5 w-5" data-id="element-156" data-mp="hgzs7qvu2g0w" />
          <p className="mt-3 text-sm font-semibold text-ink" data-id="element-157" data-mp="hjciqsfnpypk">No creative sets for {selectedOwner} yet</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-ink/55" data-id="element-158" data-mp="2t6xvovb80xmn">
            Start from an opportunity and Carli will write the brief and generate the first concepts.
          </p>
        </div> : <div className="mt-8 space-y-4" data-id="element-159" data-mp="374e3aa596xfz">
          {groups.map(group => {
        const groupSets = sets.filter(set => set.status === group.status);
        const isOpen = !collapsed[group.status] && groupSets.length > 0;
        const panelId = `ad-studio-${group.status.replace(/\s+/g, '-').toLowerCase()}`;
        return <div key={group.status} className="border-t border-ink/10 pt-4 first:border-t-0 first:pt-0" data-id="element-160" data-mp="2je0a2gaxmsjt">
                <button type="button" onClick={() => setCollapsed(current => ({
            ...current,
            [group.status]: isOpen
          }))} aria-expanded={isOpen} aria-controls={panelId} disabled={groupSets.length === 0} className="group flex w-full items-start gap-3 text-left disabled:cursor-default" data-id="element-161" data-mp="2o7356svcya24">
                  <ChevronDownIcon className={`mt-0.5 h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${isOpen ? '' : '-rotate-90'}`} aria-hidden="true" data-id="element-162" data-mp="3temhr4fdz2jv" />
                  <span className="min-w-0" data-id="element-163" data-mp="kz2uqn5b6gd9">
                    <span className="flex items-baseline gap-2" data-id="element-164" data-mp="fyevxfau8v8h">
                      <span className="text-sm font-semibold text-ink transition-colors group-hover:text-[#94452E]" data-id="element-165" data-mp="g09ckhf2u16r">{group.status}</span>
                      <span className="rounded-full bg-ink/7 px-2 py-0.5 text-[11px] font-semibold text-ink/55" data-id="element-166" data-mp="pvtaf9d7b9y5">{groupSets.length}</span>
                    </span>
                    <span className="mt-1 block text-xs text-ink/55" data-id="element-167" data-mp="2vquhg1l17l82">
                      {groupSets.length === 0 ? `Nothing here yet${selectedOwner ? ` for ${selectedOwner}` : ''}.` : group.description}
                    </span>
                  </span>
                </button>

                <AnimatePresence initial={false} data-id="element-168" data-mp="2ln73nvvkg5g2">
                  {isOpen && <motion.div id={panelId} initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.24,
              ease: [0.23, 1, 0.32, 1]
            }} className="overflow-hidden" data-id="element-169" data-mp="27t6bjkz4dgoo">
                      <div className="mt-4 grid gap-4 pb-2 sm:grid-cols-2 xl:grid-cols-3" data-id="element-170" data-mp="1owelmsp2o3bn">
                        {groupSets.map(set => <SetCard key={set.id} set={set} onOpen={() => onOpen(set)} onEndFlight={() => endFlight(set.id)} onDelete={() => deleteSet(set.id)} data-id="element-171" data-mp="3j8uy4b8godoy" />)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
      })}
        </div>}
    </section>;
}
function SetCard({
  set,
  onOpen,
  onEndFlight,
  onDelete
}: {
  set: CreativeSet;
  onOpen: () => void;
  onEndFlight: () => void;
  onDelete: () => void;
}) {
  const readyCount = set.placements.reduce((sum, placement) => sum + Object.values(placement.formats).filter(state => state === 'Ready' || state === 'Approved').length, 0);
  const totalCount = set.placements.reduce((sum, placement) => sum + Object.values(placement.formats).length, 0);
  const isPast = set.status === 'Past';
  return <article className="group flex flex-col overflow-hidden rounded-2xl border border-ink/10 bg-paper shadow-sm transition-[border-color,box-shadow] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-[#BB5E40]/45 hover:shadow-md" data-id="element-172" data-mp="s3yifqj70hyf">
      <button type="button" onClick={onOpen} className="flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BB5E40]/25" data-id="element-173" data-mp="zlmujme02jlx">
        <div className="relative aspect-[16/10] overflow-hidden bg-cream" data-id="element-174" data-mp="14nc756gvf5c4">
          <img src={set.cover} alt="" className={`h-full w-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.03] ${isPast ? 'opacity-70 grayscale' : ''}`} data-id="element-175" data-mp="19lne99fs71qn" />
          <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${statusStyles[set.status]}`} data-id="element-176" data-mp="1ncscc73i3lyv">
            {set.status}
          </span>
        </div>
        <div className="p-4" data-id="element-177" data-mp="1xb81i4iywl5q">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-178" data-mp="1x7tx6qqqu1vn">{set.brand}</p>
          <h4 className="mt-1 text-sm font-semibold text-ink" data-id="element-179" data-mp="218vx79shihyo">{set.name}</h4>
          <p className="mt-1.5 line-clamp-1 text-xs text-ink/55" data-id="element-180" data-mp="2mkdrc3u7iyjg">{isPast && set.result ? set.result : set.brief.angle}</p>
          <div className="mt-4 flex items-center justify-between text-[11px] text-ink/50" data-id="element-181" data-mp="2imy2o9u4tcts">
            <span className="inline-flex items-center gap-1.5" data-id="element-182" data-mp="2wi0jvt4fiolw">
              <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-183" data-mp="2rml7l5m2l0rl" />
              {readyCount} of {totalCount} placements
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-184" data-mp="23tcwns04dp5p">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-185" data-mp="1yy7b4uamqci5" />
              {isPast && set.ranWindow ? set.ranWindow : set.updated}
            </span>
          </div>
        </div>
      </button>

      {(isPast || set.status === 'Approved') && <div className="flex items-center justify-end border-t border-ink/10 px-4 py-2.5" data-id="element-186" data-mp="1l4y0kqay3ky1">
          {isPast ? <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-semibold text-ink/55 transition-colors hover:bg-[#BB5E40]/8 hover:text-[#94452E]" data-id="element-187" data-mp="1g6wk8d4wlzq9">
              <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-188" data-mp="3it1xp3b6ygxw" />
              Delete set
            </button> : <button type="button" onClick={onEndFlight} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-semibold text-ink/55 transition-colors hover:bg-ink/6 hover:text-ink" data-id="element-189" data-mp="3mv6olua98zgu">
              <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-190" data-mp="5ef0kn6a0ycg" />
              End flight
            </button>}
        </div>}
    </article>;
}